/** @format */

// File: AthleteModalWorkspace.jsx
// Description: Individual athlete workspace entry tracking modal supporting initial state reconciliation and closing confirmation sequences.
// Version: 3.3.0

import { useState, useEffect, useRef } from "@wordpress/element";
import Swal from "sweetalert2";

const AthleteModalWorkspace = ({
    selectedAthlete,
    entries,
    setEntries,
    meetingPostId,
    ageStandard,
    processingEventKey,
    setProcessingEventKey,
    isDeadlinePassed,
    onClose
}) => {
    const [atomicEventsList, setAtomicEventsList] = useState([]);
    const [modalLoading, setModalLoading] = useState(true);

    // Track the initial entries condition for this athlete when the modal mounts
    const initialEntriesState = useRef([]);
    const hasHydratedInitialState = useRef(false);

    // Evaluate the passed function execution pointer safely
    const deadlinePassedValue = typeof isDeadlinePassed === "function" ? isDeadlinePassed() : !!isDeadlinePassed;

    const strictNormalize = (str) => (str ? String(str).trim().toLowerCase() : "");

    const normalizeGender = (genderStr) => {
        const clean = strictNormalize(genderStr);
        if(["m", "male", "men", "boy", "boys"].includes(clean)) return "m";
        if(["f", "female", "women", "lady", "ladies", "girl", "girls"].includes(clean)) return "f";
        if(["mixed", "open", "x"].includes(clean)) return "mixed";
        return clean;
    };

    // Restored exact robust mapping tree rule matrix from AthleteManager_6.js
    const normalizeClassName = (className) => {
        if(!className) return "";
        const clean = strictNormalize(className);

        if(["under 20", "u20", "u-20", "under-20", "under 20 male", "under 20 female", "u20m", "u20f"].some((v) => clean.startsWith(v) || v.startsWith(clean))) return "under 20";
        if(["under 18", "u18", "u-18", "under-18", "under 18 male", "under 18 female", "u18m", "u18f"].some((v) => clean.startsWith(v) || v.startsWith(clean))) return "under 18";
        if(["under 15", "u15", "u-15", "under-15", "under 15 male", "under 15 female", "u15m", "u15f"].some((v) => clean.startsWith(v) || v.startsWith(clean))) return "under 15";
        if(["under 13", "u13", "u-13", "under-13", "under 13 male", "under 13 female", "u13m", "u13f"].some((v) => clean.startsWith(v) || v.startsWith(clean))) return "under 13";
        if(["class 1", "class1", "c1", "class-1"].some((v) => clean.startsWith(v) || v.startsWith(clean))) return "class 1";
        if(["class 2", "class2", "c2", "class-2"].some((v) => clean.startsWith(v) || v.startsWith(clean))) return "class 2";
        if(["class 3", "class3", "c3", "class-3"].some((v) => clean.startsWith(v) || v.startsWith(clean))) return "class 3";
        if(["class 4", "class4", "c4", "class-4"].some((v) => clean.startsWith(v) || v.startsWith(clean))) return "class 4";
        if(["class 5", "class5", "c5", "class-5"].some((v) => clean.startsWith(v) || v.startsWith(clean))) return "class 5";
        if(["senior", "snr"].includes(clean)) return "senior";
        if(["mixed", "open"].includes(clean)) return "open";

        return clean;
    };

    // Restored criteria check mechanics verbatim from AthleteManager_6.js
    const isAthleteEligibleForEvent = (athleteClassString, ageClass) => {
        if(!ageClass) return false;

        const normEvent = normalizeClassName(ageClass);
        if(normEvent === "open") return true;

        const athleteDivisions = athleteClassString
            ? String(athleteClassString)
                .split(",")
                .map((str) => normalizeClassName(str.trim()))
            : [];

        if(athleteDivisions.includes(normEvent)) return true;

        return athleteDivisions.some((div) => div.includes(normEvent) || normEvent.includes(div));
    };

    useEffect(() => {
        const globalData = window.ammData;
        if(!globalData?.api_url || !meetingPostId) return;

        setModalLoading(true);
        fetch(`${ globalData.api_url }/meetings/${ meetingPostId }/schedule`, {
            headers: { "X-WP-Nonce": globalData.nonce }
        })
            .then((res) => res.json())
            .then((scheduleData) => {
                const list = Array.isArray(scheduleData) ? scheduleData : [];
                const processed = [];
                const uniqueTracker = new Set();

                list.forEach((item) => {
                    const name = strictNormalize(item.disciplineName || item.event);
                    const type = strictNormalize(item.disciplineTypeName || "");

                    // Guard clause filtering out structural relay operations
                    if(type === "relays" || name.includes("4x") || name.includes("relay") || name.includes("medley")) return;

                    const evClass = item.ageClass || item.class || "Open";
                    const evGender = item.sexCode || item.gender || "Mixed";
                    const footprintKey = `${ name }-${ strictNormalize(evClass) }-${ normalizeGender(evGender) }`;

                    if(!uniqueTracker.has(footprintKey)) {
                        uniqueTracker.add(footprintKey);
                        processed.push({
                            ...item,
                            id: item.id,
                            rawScheduleId: item.id || item.scheduleId,
                            rawDisciplineId: item.disciplineId || item.discipline,
                            class: evClass,
                            ageClass: evClass,
                            gender: evGender
                        });
                    }
                });
                setAtomicEventsList(processed);
                setModalLoading(false);
            })
            .catch(() => setModalLoading(false));
    }, [meetingPostId]);

    // Snapshot initial state selections once eligibility checks and items load completely
    useEffect(() => {
        if(!modalLoading && atomicEventsList.length > 0 && !hasHydratedInitialState.current) {
            const initialMatches = atomicEventsList.filter((ev) => isEntered(ev));
            initialEntriesState.current = initialMatches;
            hasHydratedInitialState.current = true;
        }
    }, [modalLoading, atomicEventsList]);

    const isEntered = (ev) => {
        return entries.some((e) => {
            return (
                Number(e.athleteId) === Number(selectedAthlete.id) &&
                strictNormalize(e.disciplineId || e.event) === strictNormalize(ev.rawDisciplineId || ev.disciplineId) &&
                strictNormalize(e.ageClass || e.class) === strictNormalize(ev.class) &&
                normalizeGender(e.sexCode || e.gender) === normalizeGender(ev.gender)
            );
        });
    };

    const handleEntryToggle = (ev) => {
        if(deadlinePassedValue) return;

        const evClass = ev.class;
        const evGender = ev.gender;
        const uniqueKey = `${ strictNormalize(ev.rawDisciplineId) }-${ strictNormalize(evClass) }-${ normalizeGender(evGender) }`;

        if(processingEventKey === uniqueKey) return;
        setProcessingEventKey(uniqueKey);

        const currentlyEntered = isEntered(ev);
        const action = currentlyEntered ? "scratch" : "register";
        const globalData = window.ammData;
        const readableDisciplineName = ev.disciplineName || ev.eventName || ev.event || "Event";

        fetch(`${ globalData.api_url }/meetings/${ meetingPostId }/submit-entry`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": globalData.nonce,
            },
            body: JSON.stringify({
                meetingPostId: Number(meetingPostId),
                athleteId: Number(selectedAthlete.id),
                scheduleId: Number(ev.rawScheduleId),
                disciplineId: ev.rawDisciplineId,
                ageClass: evClass,
                gender: evGender,
                action: action,
            }),
        })
            .then((res) => res.json())
            .then((data) => {
                if(data.success) {
                    if(action === "scratch") {
                        setEntries((prev) => prev.filter((e) => {
                            return !(
                                Number(e.athleteId) === Number(selectedAthlete.id) &&
                                strictNormalize(e.disciplineId || e.event) === strictNormalize(ev.rawDisciplineId) &&
                                strictNormalize(e.ageClass || e.class) === strictNormalize(evClass) &&
                                normalizeGender(e.sexCode || e.gender) === normalizeGender(evGender)
                            );
                        }));
                    } else {
                        setEntries((prev) => [
                            ...prev,
                            {
                                athleteId: Number(selectedAthlete.id),
                                scheduleId: Number(ev.rawScheduleId),
                                disciplineId: ev.rawDisciplineId,
                                event: ev.rawDisciplineId,
                                disciplineName: readableDisciplineName,
                                displayName: readableDisciplineName,
                                ageClass: evClass,
                                class: evClass,
                                gender: evGender,
                                sexCode: evGender,
                            },
                        ]);
                    }
                } else {
                    Swal.fire({ title: "Submission Error", text: data.message || "Rejected.", icon: "error" });
                }
                setProcessingEventKey(null);
            })
            .catch(() => setProcessingEventKey(null));
    };

    // Unified Intercept Close Action Logic running sequential evaluations
    const handleWorkspaceCloseRequest = () => {
        let scratchDetected = false;
        let additionDetected = false;

        atomicEventsList.forEach((ev) => {
            const originallyChecked = initialEntriesState.current.some((initEv) => initEv.rawScheduleId === ev.rawScheduleId);
            const currentlyChecked = isEntered(ev);

            if(originallyChecked && !currentlyChecked) {
                scratchDetected = true;
            }
            if(!originallyChecked && currentlyChecked) {
                additionDetected = true;
            }
        });

        if(scratchDetected) {
            Swal.fire({
                title: "Confirm Changes",
                text: "You have deselected one or more events. Are you sure you want to save these scratch modifications?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, Confirm Changes"
            }).then((result) => {
                if(result.isConfirmed) {
                    Swal.fire({
                        title: "Success!",
                        text: "Athlete entry configurations updated effectively.",
                        icon: "success",
                        timer: 1500,
                        showConfirmButton: false
                    }).then(() => {
                        onClose();
                    });
                }
            });
        } else if(additionDetected) {
            Swal.fire({
                title: "Entries Saved",
                text: "Athlete registration profiles successfully written into the roster sheet.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false
            }).then(() => {
                onClose();
            });
        } else {
            // Safe fallback escape if no modifications occurred while workspace was opened
            onClose();
        }
    };

    return (
        <div className="amm-modal-overlay" style={ { zIndex: "110040", position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" } }>
            <div className="amm-modal-content card shadow-lg border-0 bg-white" style={ { width: "100%", maxWidth: "650px", maxHeight: "90vh" } }>
                <div className="card-header border-bottom position-relative py-3">
                    <div className="pe-5">
                        <div className="d-flex gap-3 align-items-baseline">
                            <h4 className="fw-bold mb-0 text-dark">{ selectedAthlete.lastName }, { selectedAthlete.firstName }</h4>
                            { (() => {
                                const rawGender = normalizeGender(selectedAthlete.gender);
                                const isM = rawGender === "m";
                                return (
                                    <span className={ `fw-bold text-uppercase small tracking-wider badge ${ isM ? "bg-primary bg-opacity-10 text-primary" : "bg-danger bg-opacity-10 text-danger" }` }>
                                        { isM ? "Male Pool" : "Female Pool" }
                                    </span>
                                );
                            })() }
                        </div>
                    </div>
                    <button type="button" className="btn-close position-absolute" style={ { top: "1.25rem", right: "1.25rem", background: "none", border: "none" } } onClick={ handleWorkspaceCloseRequest }>
                        <i className="fas fa-times text-muted fs-5"></i>
                    </button>
                </div>

                <div className="card-body p-4" style={ { overflowY: "auto" } }>
                    { modalLoading ? (
                        <div className="text-center py-4 text-muted"><span className="spinner-border spinner-border-sm me-2"></span>Loading eligibility criteria matches...</div>
                    ) : (
                        <div className="table-responsive bg-white rounded shadow-sm border">
                            <table className="table table-hover align-middle mb-0 small">
                                <thead className="table-light text-secondary small text-uppercase">
                                    <tr>
                                        <th className="ps-3">Event</th>
                                        <th className="text-center" style={ { width: "90px" } }>Sex</th>
                                        <th style={ { width: "130px" } }>Age Class</th>
                                        <th className="text-center pe-3" style={ { width: "90px" } }>Status</th>
                                    </tr>
                                </thead>
                                <tbody className="small">
                                    { atomicEventsList
                                        .filter((ev) => {
                                            const normEvGen = normalizeGender(ev.gender);
                                            const normAthGen = normalizeGender(selectedAthlete.gender);
                                            return normEvGen === normAthGen || normEvGen === "mixed";
                                        })
                                        .filter((ev) => isAthleteEligibleForEvent(selectedAthlete.ageClass, ev.class))
                                        .map((ev) => {
                                            const checked = isEntered(ev);
                                            const eventKey = `${ strictNormalize(ev.rawDisciplineId) }-${ strictNormalize(ev.class) }-${ normalizeGender(ev.gender) }`;
                                            const isProcessing = processingEventKey === eventKey;

                                            return (
                                                <tr key={ `modal-row-${ ev.rawScheduleId }` } className={ checked ? "table-success bg-success bg-opacity-5" : "" }>
                                                    <td className="fw-bold text-dark text-uppercase ps-3">{ ev.disciplineName || ev.event }</td>
                                                    <td className="text-center">
                                                        <span className="small text-uppercase text-muted fw-bold">{ ev.gender }</span>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-light text-secondary border text-uppercase fw-semibold px-2 py-1">{ ev.class }</span>
                                                    </td>
                                                    <td className="text-center pe-3">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input border-secondary-subtle shadow-sm"
                                                            checked={ checked }
                                                            disabled={ deadlinePassedValue || isProcessing }
                                                            onChange={ () => handleEntryToggle(ev) }
                                                            style={ { width: "1.35rem", height: "1.35rem", cursor: deadlinePassedValue || isProcessing ? "not-allowed" : "pointer" } }
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        }) }
                                    { atomicEventsList
                                        .filter((ev) => normalizeGender(ev.gender) === normalizeGender(selectedAthlete.gender) || normalizeGender(ev.gender) === "mixed")
                                        .filter((ev) => isAthleteEligibleForEvent(selectedAthlete.ageClass, ev.class)).length === 0 && (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5 text-muted fst-italic">
                                                    <i className="fas fa-exclamation-triangle d-block mb-2 text-warning fs-4"></i>
                                                    No specific individual tracking divisions match this athlete's age profile ({ selectedAthlete.ageClass || 'No Class Assigned' }).
                                                </td>
                                            </tr>
                                        ) }
                                </tbody>
                            </table>
                        </div>
                    ) }
                </div>
                <div className="card-footer bg-light p-3 text-end border-top d-flex justify-content-between align-items-center">
                    <span className="text-muted small">
                        { deadlinePassedValue ? <span className="text-danger fw-semibold"><i className="fas fa-lock me-1"></i> Read-Only Mode</span> : <span className="text-success"><i className="fas fa-check-circle me-1"></i> Auto-saving mutations</span> }
                    </span>
                    <button className="btn btn-secondary btn-sm px-4 fw-bold shadow-sm" onClick={ handleWorkspaceCloseRequest }>Close Workspace</button>
                </div>
            </div>
        </div>
    );
};

export default AthleteModalWorkspace;